/**
 * Utilitaires de normalisation des IDs d'options.
 *
 * Gère les formats historiques :
 * - UUID (id) : "a1b2c3d4-..." (BDD)
 * - Slug (option_id) : "project-management", "design-moe" (API/fallback)
 * - Legacy préfixé : "option-project-management" (ancien format localStorage)
 */

/** Retire le préfixe "option-" legacy s'il est présent */
export function normalizeOptionId(rawId: string): string {
  if (!rawId || typeof rawId !== 'string') return rawId;
  return rawId.startsWith('option-') ? rawId.slice(7) : rawId;
}

/**
 * Cherche une option dans une liste en supportant tous les formats d'ID.
 * Teste dans l'ordre : id exact, option_id exact, puis versions normalisées.
 */
export function findOptionById<T extends { id: string; option_id?: string }>(
  options: T[],
  rawId: string
): T | undefined {
  if (!Array.isArray(options) || !rawId) return undefined;
  const normalized = normalizeOptionId(rawId);

  return (
    options.find(o => o.id === rawId) ||
    options.find(o => o.option_id === rawId) ||
    options.find(o => o.id === normalized) ||
    options.find(o => o.option_id === normalized)
  );
}

/**
 * Options core intégrées au Pack (exclues du calcul complémentaire pour éviter le double comptage).
 * Inclut des alias préventifs pour éviter un futur double comptage si une option expert IA est ajoutée.
 */
export const CORE_OPTION_IDS = new Set([
  'project-management',
  'design-moe',
  'expert-ia',
  'expert_ia',
]);

/**
 * Calcule le coût total des options complémentaires (non-core) sélectionnées ou obligatoires.
 * Source unique partagée entre Navigation et Finalisation.
 *
 * Les options core (project-management, design-moe) sont exclues car elles sont
 * déjà intégrées dans le calcul des prestations Pack Ailix.
 */
export function calculateComplementaryOptionsCost(
  dynamicOptions: Array<{ id: string; price: number; is_included: boolean; option_id?: string }>,
  selectedItems: Array<{ id: string; type: 'mission' | 'option'; quantity: number }>,
): number {
  if (!Array.isArray(dynamicOptions) || !Array.isArray(selectedItems)) return 0;

  const selectedOptionNormalizedIds = new Set(
    selectedItems.filter(i => i?.type === 'option').map(i => normalizeOptionId(i.id))
  );

  let cost = 0;
  for (const opt of dynamicOptions) {
    const optKey = normalizeOptionId(opt.option_id || opt.id);
    if (CORE_OPTION_IDS.has(optKey)) continue;

    const normalizedOptId = normalizeOptionId(opt.id);
    const isSelected = selectedOptionNormalizedIds.has(normalizedOptId)
      || selectedOptionNormalizedIds.has(optKey);

    if (isSelected || opt.is_included) {
      const selectedItem = isSelected
        ? selectedItems.find(i => i?.type === 'option' && (normalizeOptionId(i.id) === normalizedOptId || normalizeOptionId(i.id) === optKey))
        : null;
      const qty = selectedItem?.quantity || 1;
      cost += (opt.price || 0) * qty;
    }
  }

  return cost;
}

/**
 * Vérifie si deux IDs d'option correspondent, en tenant compte des formats legacy.
 * Compare dans les deux sens : rawA vs rawB, normalisé(A) vs rawB, rawA vs normalisé(B), normalisé(A) vs normalisé(B).
 */
export function optionIdsMatch(idA: string, idB: string): boolean {
  if (!idA || !idB) return false;
  if (idA === idB) return true;
  const normA = normalizeOptionId(idA);
  const normB = normalizeOptionId(idB);
  return normA === idB || idA === normB || normA === normB;
}
